import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Bot, Plus, Settings, Trash2, RefreshCw, MessageSquare,
  Zap, CheckCircle2, ArrowUpRight, Clock, BarChart3, GitBranch
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { Acronym } from '../components/shared/AcronymTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const PERSONALITIES = ['professional', 'friendly', 'casual', 'formal', 'empathetic'];
const TRIGGER_TYPES = ['keyword', 'intent', 'regex', 'fallback'];
const ACTIONS = ['reply', 'escalate', 'redirect', 'collect_info', 'tag'];

const StatCard = ({ icon: Icon, label, value, color, suffix = '' }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-text-primary mt-1">{value}{suffix}</p>
        </div>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function AIChatbotPage() {
  const [config, setConfig] = useState(null);
  const [flows, setFlows] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('config');
  const [showCreateFlow, setShowCreateFlow] = useState(false);
  const [flowForm, setFlowForm] = useState({ name: '', trigger: '', trigger_type: 'keyword', response: '', action: 'reply', order: 0 });
  const [configDirty, setConfigDirty] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [c, f, a] = await Promise.all([
        axios.get(`${API}/api/ai-chatbot/config`),
        axios.get(`${API}/api/ai-chatbot/flows`),
        axios.get(`${API}/api/ai-chatbot/analytics`),
      ]);
      setConfig(c.data);
      setFlows(f.data.flows || []);
      setAnalytics(a.data);
    } catch { toast.error('Failed to load chatbot data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateConfig = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setConfigDirty(true);
  };

  const saveConfig = async () => {
    try {
      const { tenant_id, created_at, ...updates } = config;
      await axios.put(`${API}/api/ai-chatbot/config`, updates);
      toast.success('Configuration saved!');
      setConfigDirty(false);
    } catch { toast.error('Failed to save'); }
  };

  const handleCreateFlow = async () => {
    if (!flowForm.name.trim()) { toast.error('Flow name required'); return; }
    try {
      await axios.post(`${API}/api/ai-chatbot/flows`, flowForm);
      toast.success('Flow created!');
      setShowCreateFlow(false);
      setFlowForm({ name: '', trigger: '', trigger_type: 'keyword', response: '', action: 'reply', order: 0 });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const deleteFlow = async (id) => {
    if (!window.confirm('Delete this flow?')) return;
    try { await axios.delete(`${API}/api/ai-chatbot/flows/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="ai-chatbot-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary"><Acronym term="AI" /> Chatbot<PageInfoTooltip pageKey="ai-chatbot" /></h1>
          <p className="text-gray-medium text-sm mt-1">Configure and manage your automated chat assistant</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-medium">Active</span>
            <Switch checked={config?.active || false} onCheckedChange={v => updateConfig('active', v)} data-testid="chatbot-active-toggle" />
          </div>
          {configDirty && <Button size="sm" onClick={saveConfig} data-testid="save-config-btn">Save Changes</Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={MessageSquare} label="Total Sessions" value={analytics.total_sessions || 0} color="bg-blue-50 text-blue-600" />
        <StatCard icon={CheckCircle2} label="Resolved" value={analytics.resolved || 0} color="bg-emerald-50 text-emerald-600" />
        <StatCard icon={ArrowUpRight} label="Escalated" value={analytics.escalated || 0} color="bg-amber-50 text-amber-600" />
        <StatCard icon={BarChart3} label="Resolution Rate" value={analytics.resolution_rate || 0} color="bg-purple-50 text-purple-600" suffix="%" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList><TabsTrigger value="config">Configuration</TabsTrigger><TabsTrigger value="flows">Flows ({flows.length})</TabsTrigger></TabsList>

        <TabsContent value="config" className="mt-4">
          {config && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="bg-white border-gray-border">
                <CardHeader className="pb-2"><CardTitle className="text-base">Bot Settings</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div><Label>Bot Name</Label><Input value={config.name || ''} onChange={e => updateConfig('name', e.target.value)} /></div>
                  <div><Label>Welcome Message</Label><Textarea value={config.welcome_message || ''} onChange={e => updateConfig('welcome_message', e.target.value)} rows={2} /></div>
                  <div><Label>Fallback Message</Label><Textarea value={config.fallback_message || ''} onChange={e => updateConfig('fallback_message', e.target.value)} rows={2} /></div>
                  <div><Label>Personality</Label>
                    <Select value={config.personality || 'professional'} onValueChange={v => updateConfig('personality', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{PERSONALITIES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Language</Label><Input value={config.language || 'en'} onChange={e => updateConfig('language', e.target.value)} /></div>
                </CardContent>
              </Card>
              <Card className="bg-white border-gray-border">
                <CardHeader className="pb-2"><CardTitle className="text-base">Behavior</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between"><Label>Knowledge Base</Label><Switch checked={config.knowledge_base_enabled ?? true} onCheckedChange={v => updateConfig('knowledge_base_enabled', v)} /></div>
                  <div className="flex items-center justify-between"><Label>Auto-Escalate</Label><Switch checked={config.auto_escalate ?? true} onCheckedChange={v => updateConfig('auto_escalate', v)} /></div>
                  {config.auto_escalate && (
                    <div><Label>Escalation Threshold (messages)</Label><Input type="number" min={1} value={config.escalation_threshold || 3} onChange={e => updateConfig('escalation_threshold', parseInt(e.target.value))} /></div>
                  )}
                  <div className="flex items-center justify-between"><Label>24/7 Active</Label><Switch checked={config.active_24h || false} onCheckedChange={v => updateConfig('active_24h', v)} /></div>
                  {!config.active_24h && (
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Start Time</Label><Input type="time" value={config.active_hours_start || '09:00'} onChange={e => updateConfig('active_hours_start', e.target.value)} /></div>
                      <div><Label>End Time</Label><Input type="time" value={config.active_hours_end || '17:00'} onChange={e => updateConfig('active_hours_end', e.target.value)} /></div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="flows" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button onClick={() => setShowCreateFlow(true)} data-testid="create-flow-btn"><Plus className="h-4 w-4 mr-1" />New Flow</Button>
          </div>
          <Card className="bg-white border-gray-border">
            <CardContent className="pt-4">
              {flows.length === 0 ? (
                <div className="text-center py-12 text-gray-medium"><GitBranch className="h-10 w-10 mx-auto mb-3 opacity-30" /><p>No conversation flows yet</p></div>
              ) : (
                <div className="space-y-2">
                  {flows.map(flow => (
                    <div key={flow.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-border hover:bg-gray-50 transition-colors" data-testid={`flow-${flow.id}`}>
                      <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0"><GitBranch className="h-4 w-4 text-purple-600" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-text-primary">{flow.name}</span>
                          <Badge className="text-[10px] bg-blue-100 text-blue-700 capitalize">{flow.trigger_type}</Badge>
                          <Badge className="text-[10px] bg-gray-100 text-gray-600 capitalize">{flow.action}</Badge>
                        </div>
                        <p className="text-xs text-gray-medium truncate">Trigger: "{flow.trigger || '—'}" → {flow.response?.slice(0, 60) || '—'}</p>
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={() => deleteFlow(flow.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Flow Dialog */}
      <Dialog open={showCreateFlow} onOpenChange={setShowCreateFlow}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Conversation Flow</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Flow Name *</Label><Input value={flowForm.name} onChange={e => setFlowForm(f => ({ ...f, name: e.target.value }))} data-testid="flow-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Trigger Type</Label>
                <Select value={flowForm.trigger_type} onValueChange={v => setFlowForm(f => ({ ...f, trigger_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TRIGGER_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Action</Label>
                <Select value={flowForm.action} onValueChange={v => setFlowForm(f => ({ ...f, action: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ACTIONS.map(a => <SelectItem key={a} value={a} className="capitalize">{a.replace('_', ' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Trigger</Label><Input value={flowForm.trigger} onChange={e => setFlowForm(f => ({ ...f, trigger: e.target.value }))} placeholder="e.g., pricing, refund, hours" /></div>
            <div><Label>Response</Label><Textarea value={flowForm.response} onChange={e => setFlowForm(f => ({ ...f, response: e.target.value }))} rows={3} placeholder="Bot's response message..." /></div>
            <div><Label>Order</Label><Input type="number" min={0} value={flowForm.order} onChange={e => setFlowForm(f => ({ ...f, order: parseInt(e.target.value) || 0 }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreateFlow(false)}>Cancel</Button><Button onClick={handleCreateFlow} data-testid="save-flow-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
